import { AnnualCashflowRule, CALCULATION_ENGINE_VERSION, MonthlyCashflowRule, serializeCalendarResult, simulateCalendar, type CalendarResult } from '@fintracks/calculation-engine';
import { mapPlannedCashflowRulesToEngine } from '@fintracks/contracts';
import type { ScenarioAssumptions } from '@fintracks/contracts';

export function runRetirementSimulation(input: ScenarioAssumptions): CalendarResult {
  const rules = input.cashflowRules ? mapPlannedCashflowRulesToEngine(input.cashflowRules, { currentYear: input.baseYear, currentMonth: input.baseMonth, currentAge: input.currentAge, lifeExpectancyAge: input.lifeExpectancyAge }, { monthly: MonthlyCashflowRule, annual: AnnualCashflowRule }) : undefined;
  return simulateCalendar({
    currentYear: input.baseYear,
    currentMonth: input.baseMonth,
    currentAge: input.currentAge,
    retirementAge: input.retirementAge,
    lifeExpectancyAge: input.lifeExpectancyAge,
    currentReserve: input.currentReserve,
    nominalAnnualPct: input.nominalAnnualRatePct,
    inflationAnnualPct: input.inflationAnnualRatePct,
    plannedMonthlyContributionBeforeRetirement: input.monthlyContribution,
    desiredMonthlyWithdrawalAfterRetirement: input.desiredMonthlyIncome,
    differentiatePostRetirementRates: input.differentiatePostRetirementRates,
    postRetirementNominalAnnualPct: input.postRetirementNominalAnnualRatePct ?? null,
    postRetirementInflationAnnualPct: input.postRetirementInflationAnnualRatePct ?? null,
    ...(rules ?? {}),
  });
}

export { CALCULATION_ENGINE_VERSION };

export const brl = new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL', maximumFractionDigits: 0,
});
export const brlPrecise = new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL', minimumFractionDigits: 2,
});
export const number = new Intl.NumberFormat('pt-BR');
export const percent = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });
export const compactMoney = new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL', notation: 'compact', maximumFractionDigits: 1,
});

export function decimalValue(value: { toNumber(): number }): number { return value.toNumber(); }

function centsFromMoney(value: string): bigint {
  const match = /^(-?)(\d+)\.(\d{2})$/.exec(value);
  if (!match) throw new Error('Valor monetário persistido inválido');
  const cents = BigInt(match[2]!) * 100n + BigInt(match[3]!);
  return match[1] === '-' ? -cents : cents;
}

function moneyFromCents(value: bigint): string {
  const sign = value < 0n ? '-' : '';
  const absolute = value < 0n ? -value : value;
  return `${sign}${absolute / 100n}.${String(absolute % 100n).padStart(2, '0')}`;
}

export function sumMoney(values: readonly string[]): string {
  return moneyFromCents(values.reduce((total, value) => total + centsFromMoney(value), 0n));
}

export function subtractMoneyStrings(left: string, right: string): string {
  return moneyFromCents(centsFromMoney(left) - centsFromMoney(right));
}

export function divideMoneyString(value: string, divisor: bigint): string {
  if (divisor <= 0n) throw new Error('Divisor monetário inválido');
  const cents = centsFromMoney(value);
  const sign = cents < 0n ? -1n : 1n;
  const absolute = cents < 0n ? -cents : cents;
  let quotient = absolute / divisor;
  if ((absolute % divisor) * 2n >= divisor) quotient += 1n;
  return moneyFromCents(sign * quotient);
}

export function parseMoneyInput(input: string): number | undefined {
  const raw = input.trim().replace(/\s/g, '');
  if (!raw || !/^\d+(?:[.,]\d{1,2})?$/.test(raw)) return undefined;
  const normalized = raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw;
  const value = Number(normalized);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

export function formatDate(value: string): string {
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value));
}

export function serializeRetirementResult(result: CalendarResult) {
  return serializeCalendarResult(result);
}
